if (!customElements.get('custom-product-videos')) {
  customElements.define(
    'custom-product-videos',
    class CustomProductVideos extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.track = this.querySelector('[data-video-track]');
        this.cards = Array.from(this.querySelectorAll('[data-video-index]'));
        this.previousButton = this.querySelector('[data-video-previous]');
        this.nextButton = this.querySelector('[data-video-next]');
        this.modal = this.querySelector('[data-video-modal]');
        this.panel = this.querySelector('[data-video-panel]');
        this.player = this.querySelector('[data-video-player]');
        this.overlayToggle = this.querySelector('[data-video-overlay-toggle]');
        this.imageViewer = this.querySelector('[data-image-viewer]');
        this.closeButton = this.querySelector('[data-video-close]');
        this.modalPrevious = this.querySelector('[data-modal-previous]');
        this.modalNext = this.querySelector('[data-modal-next]');
        this.videoStatus = this.querySelector('[data-video-status]');
        this.videoStatusMessage = this.querySelector('[data-video-status-message]');
        this.activeIndex = 0;
        this.dragDistance = 0;
        this.wheelLocked = false;
        this.mediaSwipeDistance = 0;
        if (!this.track || !this.cards.length || !this.modal || !this.panel || !this.player || !this.imageViewer) return;

        this.eventController = new AbortController();
        const listenerOptions = { signal: this.eventController.signal };

        this.onScroll = this.updateArrows.bind(this);
        this.onKeydown = this.handleKeydown.bind(this);
        this.onWheel = this.handleWheel.bind(this);
        this.track.addEventListener('scroll', this.onScroll, { passive: true, signal: this.eventController.signal });
        this.previousButton?.addEventListener('click', () => this.scrollTrack(-1), listenerOptions);
        this.nextButton?.addEventListener('click', () => this.scrollTrack(1), listenerOptions);
        this.cards.forEach((card) => card.addEventListener('click', () => {
          if (this.dragDistance < 6) this.open(Number(card.dataset.videoIndex));
        }, listenerOptions));
        this.closeButton?.addEventListener('click', () => this.close(), listenerOptions);
        this.modalPrevious?.addEventListener('click', () => this.changeMedia(-1), listenerOptions);
        this.modalNext?.addEventListener('click', () => this.changeMedia(1), listenerOptions);
        this.overlayToggle?.addEventListener('click', (event) => {
          event.stopPropagation();
          this.toggleVideoPlayback();
          this.showOverlayBriefly();
        }, listenerOptions);
        this.modal.addEventListener('click', (event) => {
          if (event.target === this.modal) this.close();
        }, listenerOptions);
        this.modal.addEventListener('wheel', this.onWheel, { passive: false, signal: this.eventController.signal });
        this.player.addEventListener('click', (event) => this.handleVideoSurfaceClick(event), listenerOptions);
        this.player.addEventListener('play', () => this.syncOverlayState(), listenerOptions);
        this.player.addEventListener('playing', () => this.syncOverlayState(), listenerOptions);
        this.player.addEventListener('pause', () => this.syncOverlayState(), listenerOptions);
        this.player.addEventListener('waiting', () => this.syncOverlayState(true), listenerOptions);
        this.player.addEventListener('loadedmetadata', () => this.handleMetadataLoaded(), listenerOptions);
        this.player.addEventListener('durationchange', () => this.reportInvalidDuration(), listenerOptions);
        this.player.addEventListener('canplay', () => this.hideVideoStatus(), listenerOptions);
        this.player.addEventListener('error', () => this.handleVideoError(), listenerOptions);
        this.enableCarouselDrag(listenerOptions);
        this.enableVerticalSwipe(listenerOptions);
        this.resizeObserver = new ResizeObserver(() => this.updateArrows());
        this.resizeObserver.observe(this.track);
        document.body.appendChild(this.modal);
        this.updateArrows();
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
        document.removeEventListener('keydown', this.onKeydown);
        if (this.modal && !this.modal.hidden) this.close(false);
        if (this.modal && this.modal.parentNode !== this) this.appendChild(this.modal);
        this.eventController?.abort();
        this.initialized = false;
      }

      scrollTrack(direction) {
        const first = this.cards[0];
        if (!first) return;
        const gap = Number.parseFloat(getComputedStyle(this.track).columnGap) || 0;
        this.track.scrollBy({ left: direction * (first.getBoundingClientRect().width + gap), behavior: 'smooth' });
      }

      updateArrows() {
        const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
        if (this.previousButton) this.previousButton.disabled = this.track.scrollLeft <= 2;
        if (this.nextButton) this.nextButton.disabled = this.track.scrollLeft >= maxScroll - 2;
      }

      enableCarouselDrag(listenerOptions) {
        let startX = 0;
        let startScroll = 0;
        let dragging = false;
        let captured = false;
        this.track.addEventListener('pointerdown', (event) => {
          if (event.pointerType === 'touch') return;
          dragging = true;
          captured = false;
          this.dragDistance = 0;
          startX = event.clientX;
          startScroll = this.track.scrollLeft;
        }, listenerOptions);
        this.track.addEventListener('pointermove', (event) => {
          if (!dragging) return;
          this.dragDistance = Math.abs(event.clientX - startX);
          if (!captured && this.dragDistance >= 6) {
            captured = true;
            this.track.classList.add('is-dragging');
            this.track.setPointerCapture(event.pointerId);
          }
          if (!captured) return;
          this.track.scrollLeft = startScroll - (event.clientX - startX);
        }, listenerOptions);
        const finish = (event) => {
          if (!dragging) return;
          dragging = false;
          this.track.classList.remove('is-dragging');
          if (captured && this.track.hasPointerCapture(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
          captured = false;
          window.setTimeout(() => { this.dragDistance = 0; }, 0);
        };
        this.track.addEventListener('pointerup', finish, listenerOptions);
        this.track.addEventListener('pointercancel', finish, listenerOptions);
      }

      enableVerticalSwipe(listenerOptions) {
        let startY = null;
        let pointerId = null;
        let captured = false;
        this.panel.addEventListener('pointerdown', (event) => {
          if (event.target.closest('button, a')) return;
          startY = event.clientY;
          pointerId = event.pointerId;
          captured = false;
          this.mediaSwipeDistance = 0;
        }, listenerOptions);
        this.panel.addEventListener('pointermove', (event) => {
          if (startY === null) return;
          this.mediaSwipeDistance = Math.abs(event.clientY - startY);
          if (captured || this.mediaSwipeDistance < 12) return;
          captured = true;
          this.panel.setPointerCapture(event.pointerId);
        }, listenerOptions);
        const finish = (event) => {
          if (startY === null) return;
          const distance = event.clientY - startY;
          startY = null;
          pointerId = null;
          if (captured && this.panel.hasPointerCapture(event.pointerId)) this.panel.releasePointerCapture(event.pointerId);
          captured = false;
          if (Math.abs(distance) >= 45) this.changeMedia(distance < 0 ? 1 : -1);
          window.setTimeout(() => { this.mediaSwipeDistance = 0; }, 0);
        };
        this.panel.addEventListener('pointerup', finish, listenerOptions);
        this.panel.addEventListener('pointercancel', () => {
          if (captured && pointerId !== null && this.panel.hasPointerCapture(pointerId)) this.panel.releasePointerCapture(pointerId);
          startY = null;
          pointerId = null;
          captured = false;
          this.mediaSwipeDistance = 0;
        }, listenerOptions);
      }

      open(index) {
        this.returnFocus = document.activeElement;
        this.previousRootPaddingRight = document.documentElement.style.paddingRight;
        const scrollbarWidth = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
        if (scrollbarWidth) document.documentElement.style.paddingRight = `${scrollbarWidth}px`;
        this.modal.hidden = false;
        document.documentElement.classList.add('custom-product-video-open');
        document.addEventListener('keydown', this.onKeydown);
        this.loadMedia(index, true);
        this.closeButton?.focus({ preventScroll: true });
      }

      close(restoreFocus = true) {
        this.player.pause();
        try { this.player.currentTime = 0; } catch (_error) { /* Media was not ready. */ }
        this.clearVideo();
        this.imageViewer.removeAttribute('src');
        this.hideVideoStatus();
        this.imageViewer.alt = '';
        this.modal.hidden = true;
        document.documentElement.classList.remove('custom-product-video-open');
        document.documentElement.style.paddingRight = this.previousRootPaddingRight || '';
        document.removeEventListener('keydown', this.onKeydown);
        if (restoreFocus && this.returnFocus instanceof HTMLElement) this.returnFocus.focus({ preventScroll: true });
      }

      loadMedia(index, autoplay) {
        const total = this.cards.length;
        this.activeIndex = (index + total) % total;
        const card = this.cards[this.activeIndex];
        const mediaType = card.dataset.mediaType || 'video';
        this.player.pause();
        this.clearVideo();
        this.imageViewer.removeAttribute('src');
        this.hideVideoStatus();

        if (mediaType === 'image') {
          this.player.hidden = true;
          this.imageViewer.hidden = false;
          this.imageViewer.src = card.dataset.imageUrl || '';
          this.imageViewer.alt = card.dataset.imageAlt || '';
        } else {
          this.imageViewer.hidden = true;
          this.player.hidden = false;
          let sources = [];
          try {
            sources = JSON.parse(card.dataset.videoSources || '[]');
          } catch (error) {
            console.warn('See How It Works: invalid Shopify video.sources JSON.', error);
          }
          const source = this.selectVideoSource(sources);
          const poster = card.querySelector('.custom-product-videos__poster img');
          if (poster) this.player.poster = poster.currentSrc || poster.src;
          if (!source) {
            console.warn('See How It Works: no browser-compatible Shopify video source was found.', sources);
            this.syncNavigationState();
            return;
          }
          this.player.src = source.url;
          this.player.load();
          this.syncOverlayState();
          if (autoplay) {
            const playPromise = this.player.play();
            if (playPromise && typeof playPromise.catch === 'function') {
              playPromise.catch((error) => {
                console.info('See How It Works: native playback awaits user interaction.', error);
                this.syncOverlayState(true);
              });
            }
          }
        }
        this.syncNavigationState();
      }

      selectVideoSource(sources) {
        const normalized = (Array.isArray(sources) ? sources : [])
          .filter((source) => source && source.url)
          .map((source) => {
            const format = String(source.format || '').toLowerCase();
            const url = String(source.url);
            let type = String(source.mime_type || source.mimeType || '').toLowerCase();
            if (!type && (format === 'mp4' || /\.mp4(?:$|\?)/i.test(url))) type = 'video/mp4';
            if (!type && (format === 'm3u8' || /\.m3u8(?:$|\?)/i.test(url))) type = 'application/vnd.apple.mpegurl';
            return { url, type };
          });
        const mp4 = normalized.find((source) => source.type === 'video/mp4' || /\.mp4(?:$|\?)/i.test(source.url));
        if (mp4) return mp4;
        const nativeHls = normalized.find((source) => source.type.includes('mpegurl') && this.player.canPlayType(source.type) !== '');
        if (nativeHls) return nativeHls;
        return normalized.find((source) => !source.type || this.player.canPlayType(source.type) !== '') || null;
      }

      handleVideoError() {
        if (this.player.hidden || !this.player.src || !this.player.error) return;
        const errorCode = this.player.error.code;
        console.warn('See How It Works: Shopify video failed to load or decode.', {
          code: errorCode,
          currentSrc: this.player.currentSrc || this.player.src,
          networkState: this.player.networkState,
          readyState: this.player.readyState
        });
        if (!this.videoStatus) return;
        this.videoStatus.hidden = false;
        if (this.videoStatusMessage) this.videoStatusMessage.textContent = `Video unavailable (error ${errorCode})`;
        this.syncOverlayState(true);
      }

      handleMetadataLoaded() {
        this.hideVideoStatus();
        this.reportInvalidDuration();
        this.syncOverlayState();
      }

      reportInvalidDuration() {
        if (this.player.readyState < HTMLMediaElement.HAVE_METADATA) return;
        if (!Number.isFinite(this.player.duration) || this.player.duration <= 0) {
          console.warn('See How It Works: Shopify MP4 metadata did not provide a valid duration.', {
            currentSrc: this.player.currentSrc || this.player.src,
            duration: this.player.duration,
            networkState: this.player.networkState,
            readyState: this.player.readyState
          });
        }
      }

      handleVideoSurfaceClick(event) {
        if (this.player.hidden || this.mediaSwipeDistance >= 12) return;
        const bounds = this.player.getBoundingClientRect();
        const nativeControlHeight = 56;
        if (event.clientY >= bounds.bottom - nativeControlHeight) return;
        this.toggleVideoPlayback();
        this.showOverlayBriefly();
      }

      toggleVideoPlayback() {
        if (this.player.hidden || !this.player.src) return;
        if (!this.player.paused) {
          this.player.pause();
          return;
        }
        const playPromise = this.player.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch((error) => {
            console.info('See How It Works: playback remains blocked; use the native controls.', error);
            this.syncOverlayState(true);
          });
        }
      }

      syncOverlayState(forceVisible = false) {
        if (!this.overlayToggle) return;
        const paused = this.player.paused || this.player.ended;
        this.player.classList.toggle('is-paused', paused);
        this.player.classList.toggle('is-overlay-visible', forceVisible);
        this.overlayToggle.setAttribute('aria-label', paused ? 'Play video' : 'Pause video');
      }

      showOverlayBriefly() {
        if (!this.overlayToggle) return;
        this.player.classList.add('is-overlay-visible');
        window.clearTimeout(this.overlayTimer);
        this.overlayTimer = window.setTimeout(() => {
          this.player.classList.remove('is-overlay-visible');
        }, 1200);
      }

      hideVideoStatus() {
        if (this.videoStatus) this.videoStatus.hidden = true;
      }

      clearVideo() {
        window.clearTimeout(this.overlayTimer);
        this.player.classList.remove('is-paused', 'is-overlay-visible');
        this.player.removeAttribute('src');
        this.player.removeAttribute('poster');
        this.player.querySelectorAll('source').forEach((source) => source.remove());
        this.player.load();
      }

      changeMedia(direction) {
        if (this.cards.length < 2) return;
        this.loadMedia(this.activeIndex + direction, true);
      }

      handleWheel(event) {
        if (this.wheelLocked || Math.abs(event.deltaY) < 18 || this.cards.length < 2) return;
        event.preventDefault();
        this.wheelLocked = true;
        this.changeMedia(event.deltaY > 0 ? 1 : -1);
        window.setTimeout(() => { this.wheelLocked = false; }, 550);
      }

      syncNavigationState() {
        const hide = this.cards.length < 2;
        if (this.modalPrevious) this.modalPrevious.hidden = hide;
        if (this.modalNext) this.modalNext.hidden = hide;
      }

      handleKeydown(event) {
        if (event.key === 'Escape') this.close();
        if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') this.changeMedia(-1);
        if (event.key === 'ArrowDown' || event.key === 'ArrowRight') this.changeMedia(1);
        if (event.key !== 'Tab') return;
        const focusable = Array.from(this.modal.querySelectorAll('button:not([hidden]):not([disabled]), a[href]:not([hidden])'));
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }
  );
}
